import { useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'

import type { FC } from 'react'

export type TOrganizationProgressStep =
  | 'STEP_PENILAIAN'
  | 'STEP_PENETAPAN_PS'
  | 'STEP_PENETAPAN_DINAS'

type Props = {
  activeStep: TOrganizationProgressStep
}

type StepIconProps = {
  state: 'IN_PROGRESS' | 'LOCKED' | 'DONE'
}

const Ping = () => {
  return (
    <span
      className="relative flex-none flex items-center justify-center h-7 w-7"
      role="presentation"
    >
      <span
        className="absolute animate-ping h-5 w-5 rounded-full bg-blue-30"
        style={{ animationDuration: '2s' }}
      />
      <span className="relative rounded-full h-4 w-4 bg-interactive-default" />
    </span>
  )
}

const StepIcon = ({ state }: StepIconProps) => {
  switch (state) {
    case 'IN_PROGRESS':
      return <Ping />
    case 'DONE':
      return (
        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-20 flex-none">
          <Icon
            className="!w-4 !h-4 !text-base !leading-4"
            color="informational"
          >
            checked
          </Icon>
        </div>
      )
    default:
      return (
        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-10 flex-none text-gray-50">
          <Icon className="!w-4 !h-4 !text-base !leading-4" color="inherit">
            locked_outline
          </Icon>
        </div>
      )
  }
}

const ORGANIZATION_PROGRESS_STEPS: Record<
  TOrganizationProgressStep,
  { title: string; description: string | React.ReactNode; order: number }
> = {
  STEP_PENILAIAN: {
    title: 'Pengiriman Rekomendasi Predikat Kinerja ke Dinas Pendidikan',
    description:
      'Dilakukan oleh Tim Kerja (Pengawas Sekolah) setelah seluruh Pegawai selesai dinilai. Pengiriman bukan tahap akhir dan dilakukan hanya agar Dinas Pendidikan dapat melanjutkan proses.',
    order: 1,
  },
  STEP_PENETAPAN_PS: {
    title: 'Penetapan Predikat Kinerja Pegawai',
    description: (
      <>
        Dilakukan oleh Dinas Pendidikan berdasarkan rekomendasi dari Tim Kerja
        (Pengawas Sekolah). Ini adalah <strong>tahap akhir</strong> yang
        dilakukan untuk Penilaian Kinerja.
      </>
    ),
    order: 2,
  },
  STEP_PENETAPAN_DINAS: {
    title: 'Penerimaan Dokumen Evaluasi Kinerja',
    description:
      'Dokumen Evaluasi Kinerja Pegawai untuk periode yang sedang berlangsung akan diterima oleh masing-masing Pegawai.',
    order: 3,
  },
}

const CardOrganizationProgress: FC<Props> = ({ activeStep }) => {
  const [isOpen, setOpen] = useState(false)

  const progressSteps = Object.entries(ORGANIZATION_PROGRESS_STEPS)
  const activeStepObj = ORGANIZATION_PROGRESS_STEPS[activeStep]
  const activeStepOrder = activeStepObj?.order ?? 0
  const activeStepTitle = activeStepObj?.title
  const activeStepDescription = activeStepObj?.description
  const isCompleted = activeStep === 'STEP_PENETAPAN_DINAS'

  const cardTitle = isCompleted
    ? 'Atasan sudah menyelesaikan tahap terakhir:'
    : 'Saat ini atasan sedang melanjutkan tahap:'

  return (
    <>
      <Card className="py-4 px-6 !flex-row space-x-4">
        <div className="w-full max-w-md flex-1 space-y-4">
          <div className="space-y-1">
            <Text
              variant="heading-md"
              className="!text-black"
              as="h2"
              aria-label={cardTitle + ' ' + activeStepTitle}
            >
              {cardTitle}
            </Text>
            {!isCompleted && (
              <Text color="subdued">
                Diskusikan tahap-tahap ini dengan atasan jika diperlukan.
              </Text>
            )}
          </div>
          <Button
            size="sm"
            aria-label="Lihat tahap selengkapnya"
            onClick={() => setOpen(true)}
          >
            Selengkapnya
          </Button>
        </div>
        <div className="flex-1 flex space-x-4 max-w-xl">
          <StepIcon state={isCompleted ? 'DONE' : 'IN_PROGRESS'} />
          <div>
            <Text variant="action" className="mb-1" aria-hidden="true">
              {activeStepTitle}
            </Text>
            <Text color="subdued">{activeStepDescription}</Text>
          </div>
        </div>
      </Card>

      <Dialog isOpen={isOpen} size="md">
        <DialogOverlay />
        <DialogContent>
          <DialogHeader
            title="Rangkaian tahap Penilaian Kinerja"
            borderless={true}
          />
          <div className="p-6">
            <ol className="mb-2">
              {progressSteps.map(([key, detail], i) => {
                let state: StepIconProps['state'] = 'LOCKED'

                if (
                  detail.order < activeStepOrder ||
                  activeStep === 'STEP_PENETAPAN_DINAS' // Always completed
                ) {
                  state = 'DONE'
                } else if (detail.order === activeStepOrder) {
                  state = 'IN_PROGRESS'
                }

                return (
                  <li
                    key={key}
                    className="flex space-x-4"
                    aria-current={state === 'IN_PROGRESS' ? 'step' : undefined}
                  >
                    <div
                      role="presentation"
                      className="flex flex-col items-center"
                    >
                      <StepIcon state={state} />
                      {i < progressSteps.length - 1 && (
                        <div className="w-px bg-gray-20 h-full" />
                      )}
                    </div>
                    <div className="mb-4">
                      <Text as="h3" variant="action" className="mb-1">
                        {detail.title}
                      </Text>
                      <Text color="subdued">{detail.description}</Text>
                    </div>
                  </li>
                )
              })}
            </ol>
            <Button
              size="lg"
              className="mx-auto"
              onClick={() => setOpen(false)}
            >
              Saya mengerti
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
export default CardOrganizationProgress
